// ABOUTME: Custom tool UI components for LangGraph backend tools
// ABOUTME: Provides styled rendering for Neo4j queries and schema retrieval

import { makeAssistantToolUI } from "@assistant-ui/react";
import { Database, FileCode, CheckIcon, ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

// Neo4j Query Tool UI
export const Neo4jQueryToolUI = makeAssistantToolUI<
  { cypher_query: string },
  string
>({
  toolName: "query_neo4j_database",
  render: ({ args, result, status }) => {
    const [isOpen, setIsOpen] = useState(false);

    const cypher_query = args.cypher_query || "";

    // Format result - try to parse and pretty-print JSON
    let resultText = result || "";
    try {
      const parsed = JSON.parse(resultText);
      resultText = JSON.stringify(parsed, null, 2);
    } catch {
      // Not JSON, use as-is
    }

    return (
      <Card className="mb-2 w-full border-l-4 border-l-blue-500">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CardHeader className="py-2 px-3">
            <CollapsibleTrigger className="flex w-full items-center justify-between hover:opacity-70 transition-opacity">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <Database className="size-4" />
                <span>Interviews Query</span>
                {status.type === "complete" && <CheckIcon className="size-3 text-green-600" />}
                {status.type === "running" && (
                  <span className="text-xs text-muted-foreground">Running...</span>
                )}
              </CardTitle>
              {isOpen ? (
                <ChevronDownIcon className="size-4 text-muted-foreground" />
              ) : (
                <ChevronRightIcon className="size-4 text-muted-foreground" />
              )}
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="space-y-2 pt-0 px-3 pb-2">
              {/* Query section */}
              <details className="group">
                <summary className="cursor-pointer text-xs font-semibold text-muted-foreground hover:text-foreground mb-1">
                  View Query
                </summary>
                <pre className="rounded-md bg-muted/50 p-2 text-xs overflow-x-auto mt-1 font-mono whitespace-pre-wrap">
                  {cypher_query}
                </pre>
              </details>

              {/* Results section */}
              {result && (
                <div>
                  <p className="mb-1 text-sm font-semibold text-foreground">
                    Results
                  </p>
                  <pre className="rounded-md bg-background border p-2 text-xs overflow-x-auto max-h-64 font-mono whitespace-pre-wrap">
                    {resultText}
                  </pre>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  },
});

// Recommendations Query Tool UI
export const RecommendationsQueryToolUI = makeAssistantToolUI<
  { cypher_query: string },
  string
>({
  toolName: "query_recommendations_database",
  render: ({ args, result, status }) => {
    const [isOpen, setIsOpen] = useState(false);
    const cypher_query = args.cypher_query || "";

    // Format result - try to parse and pretty-print JSON
    let resultText = result || "";
    try {
      const parsed = JSON.parse(resultText);
      resultText = JSON.stringify(parsed, null, 2);
    } catch {
      // Not JSON, use as-is
    }

    return (
      <Card className="mb-2 w-full border-l-4 border-l-cyan-500">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CardHeader className="py-2 px-3">
            <CollapsibleTrigger className="flex w-full items-center justify-between hover:opacity-70 transition-opacity">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <Database className="size-4" />
                <span>Recommendations Query</span>
                {status.type === "complete" && <CheckIcon className="size-3 text-green-600" />}
                {status.type === "running" && (
                  <span className="text-xs text-muted-foreground">Running...</span>
                )}
              </CardTitle>
              {isOpen ? (
                <ChevronDownIcon className="size-4 text-muted-foreground" />
              ) : (
                <ChevronRightIcon className="size-4 text-muted-foreground" />
              )}
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="space-y-2 pt-0 px-3 pb-2">
              <details className="group">
                <summary className="cursor-pointer text-xs font-semibold text-muted-foreground hover:text-foreground mb-1">
                  View Query
                </summary>
                <pre className="rounded-md bg-muted/50 p-2 text-xs overflow-x-auto mt-1 font-mono whitespace-pre-wrap">
                  {cypher_query}
                </pre>
              </details>
              {result && (
                <div>
                  <p className="mb-1 text-sm font-semibold text-foreground">Results</p>
                  <pre className="rounded-md bg-background border p-2 text-xs overflow-x-auto max-h-64 font-mono whitespace-pre-wrap">
                    {resultText}
                  </pre>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  },
});

// Schema Tool UI
export const SchemaToolUI = makeAssistantToolUI<
  Record<string, never>,
  string
>({
  toolName: "get_database_schema",
  render: ({ result, status }) => {
    const [isOpen, setIsOpen] = useState(false);

    const resultText = result || "";

    return (
      <Card className="mb-2 w-full border-l-4 border-l-purple-500">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CardHeader className="py-2 px-3">
            <CollapsibleTrigger className="flex w-full items-center justify-between hover:opacity-70 transition-opacity">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <FileCode className="size-4" />
                <span>Interviews Schema Retrieved</span>
                {status.type === "complete" && <CheckIcon className="size-3 text-green-600" />}
                {status.type === "running" && (
                  <span className="text-xs text-muted-foreground">Loading...</span>
                )}
              </CardTitle>
              {isOpen ? (
                <ChevronDownIcon className="size-4 text-muted-foreground" />
              ) : (
                <ChevronRightIcon className="size-4 text-muted-foreground" />
              )}
            </CollapsibleTrigger>
            {!isOpen && (
              <p className="text-xs text-muted-foreground mt-0.5">
                FIG interview database schema
              </p>
            )}
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="space-y-2 pt-0 px-3 pb-2">
              {result && (
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <pre className="rounded-md bg-background border p-3 text-xs overflow-x-auto max-h-96 whitespace-pre-wrap">
                    {resultText}
                  </pre>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  },
});

// Recommendations Schema Tool UI
export const RecommendationsSchemaToolUI = makeAssistantToolUI<
  Record<string, never>,
  string
>({
  toolName: "get_recommendations_schema",
  render: ({ result, status }) => {
    const [isOpen, setIsOpen] = useState(false);
    const resultText = result || "";

    return (
      <Card className="mb-2 w-full border-l-4 border-l-indigo-500">
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CardHeader className="py-2 px-3">
            <CollapsibleTrigger className="flex w-full items-center justify-between hover:opacity-70 transition-opacity">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <FileCode className="size-4" />
                <span>Recommendations Schema Retrieved</span>
                {status.type === "complete" && <CheckIcon className="size-3 text-green-600" />}
                {status.type === "running" && (
                  <span className="text-xs text-muted-foreground">Loading...</span>
                )}
              </CardTitle>
              {isOpen ? (
                <ChevronDownIcon className="size-4 text-muted-foreground" />
              ) : (
                <ChevronRightIcon className="size-4 text-muted-foreground" />
              )}
            </CollapsibleTrigger>
            {!isOpen && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Recommendations database schema
              </p>
            )}
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="space-y-2 pt-0 px-3 pb-2">
              {result && (
                <div className="prose prose-sm max-w-none dark:prose-invert">
                  <pre className="rounded-md bg-background border p-3 text-xs overflow-x-auto max-h-96 whitespace-pre-wrap">
                    {resultText}
                  </pre>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  },
});
